// External Imports
import { Fragment, useContext, useState } from "react";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import { DataContext } from "../../context/data-context";
import Modal from "../UI/Modal";
import { MdCheck, MdClose, MdEdit } from "react-icons/md";
import LinkPair from "../UI/LinkPair";
import { requestAdminAPI } from "../../api/posh-api";
import uploadFile from "../../util/upload-file";
import LoadingSpinner from "../UI/LoadingSpinner";
import { isoDateToString } from "../../util/format-date";
import { formatCurrency, toTitleCase } from "../../util/format-text";
import Departments from "./departments";

// Internal Imports

/** ========================================================================
 * * Manage Employee Modal
 * This component renders a page manage an employee.
 * ========================================================================== */

export default function ManageEmployeeModal({ employee, open, setOpen, onComplete }) {
  // Load Context
  const { locations } = useContext(DataContext);
  // Initialze States
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);

  /* ====================== MANAGE EMPLOYEE ====================== */
  const submitHandler = async () => {
    try {
      const payload = {
        id: employee.id,
        locationId: document.getElementById("locationId").value,
        type: document.getElementById("type").value?.toLowerCase(),
        title: document.getElementById("title").value,
        compensationFrequency: document.getElementById("compensationFrequency")
          .value,
        compensationAmount:
          parseFloat(document.getElementById("compensationAmount").value) || 0,
        ghlId: document.getElementById("ghlId").value || null,
      };
      setLoading(true);
      const files = [
        {
          name: "contract",
          file: document.getElementById("contract")?.files[0],
        },
      ];
      for (const file of files) {
        if (!file.file) continue;
        const url = await uploadFile(
          `employees/${employee.id}/${file.name}.${file.file.name
            .split(".")
            .pop()}`,
          file.file,
          () => {},
        );
        payload[file.name] = url;
      }
      await requestAdminAPI("manage-employee", payload);
      setEdit(false);
      onComplete();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== DELETE ADMIN ACCOUNT ====================== */
  const suspendHandler = async (account) => {
    try {
      setLoading(true);
      await requestAdminAPI("suspend-employee", { id: employee.id, account });
      onComplete();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal  lg:w-[1200px]">
        <Heading
          title="Manage Employee"
          isBackArrow={false}
          text="Only users with authorized access can manage employee records."
          id={employee?.id}
        >
          {loading ? (
            <LoadingSpinner classes="text-white w-5 h-5" />
          ) : edit ? (
            <Fragment>
              <MdClose className="icon" onClick={() => setEdit(false)} />
              <MdCheck className="icon" onClick={submitHandler} />
            </Fragment>
          ) : (
            <Fragment>
              <MdEdit className="icon" onClick={() => setEdit(true)} />
              <MdClose className="icon" onClick={() => setOpen(false)} />
            </Fragment>
          )}
        </Heading>

        <div className="items-end w-full gap-x-8 info lg:grid-cols-4">
          <DetailPair
            label="Date Created"
            value={isoDateToString(employee?.dateCreated)}
          />
          <DetailPair label="Full Name" value={employee?.name} />
          <DetailPair label="Email Address" value={employee?.email} />
          <SelectPair
            label="Location"
            value={employee?.location?.name}
            options={locations.map((location) => ({
              id: location.id,
              name: location.name,
            }))}
            edit={edit}
            inputOptions={{
              defaultValue: employee?.locationId,
              id: "locationId",
              required: true,
            }}
            loading={loading}
          />
          <SelectPair
            label="Department"
            value={toTitleCase(employee?.type)}
            options={Departments.departments}
            edit={edit}
            inputOptions={{
              defaultValue: toTitleCase(employee?.type),
              id: "type",
              required: true,
            }}
            loading={loading}
          />
          <DetailPair
            label="Title"
            value={employee?.title}
            loading={loading}
            edit={edit}
            inputOptions={{
              defaultValue: employee?.title,
              id: "title",
            }}
          />
          <SelectPair
            label="Compensation Frequency"
            value={employee?.compensationFrequency}
            options={["Hourly", "Weekly", "Bi-Weekly", "Monthly", "Per-diem"]}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "compensationFrequency",
              defaultValue: employee?.compensationFrequency,
            }}
          />
          <DetailPair
            label="Compensation Amount"
            value={formatCurrency(employee?.compensationAmount)}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "compensationAmount",
              type: "number",
              step: "0.01",
              defaultValue: employee?.compensationAmount,
            }}
          />
          <LinkPair
            label="Employment Contract"
            link={employee?.contract}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "contract",
              type: "file",
            }}
          />
          <DetailPair
            label="GHL ID"
            value={employee?.ghlId}
            loading={loading}
            edit={edit}
            inputOptions={{
              defaultValue: employee?.ghlId,
              id: "ghlId",
            }}
          />
        </div>

        {loading ? (
          <div className="mt-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="mt-8 info">
            {employee?.ghlId && (
              <div
                onClick={() => suspendHandler("ghl")}
                className="btn-secondary"
              >
                Suspend GHL Account
              </div>
            )}
            {employee?.active && <div
              onClick={() => suspendHandler("admin")}
              className="btn-warning"
            >
              Suspend Admin Account
            </div>}
            <div
              onClick={() => suspendHandler("google")}
              className="btn-warning"
            >
              Suspend Google Account
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
